import React, { useEffect } from 'react';

import { useAppDispatch, useAppSelector } from 'hooks/store';
import { ICallScribeCallListItem } from 'services/callScribe/types';
import {
  CallScribeTranscriptForCallSelector,
  CallScribeTranscriptLoadingForCallSelector,
} from 'store/callScribe/selectors';
import { ThunkGetCallScribeTranscript } from 'store/callScribe/thunks';

import ExpandLessRoundedIcon from '@mui/icons-material/ExpandLessRounded';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';

import usePaperStyles from 'components/CallScribe/paperStyles';
import { transcriptCaption } from 'components/CallScribe/transcriptCaption';

export interface ICallReviewPanelProps {
  call: ICallScribeCallListItem;
}

/**
 * Read-only post-call review surface. Renders the AI-generated customer-facing summary
 * and the internal/private summary, plus a lazy "Show transcript" expander that fetches
 * the redacted transcript on first open. Sits above the editable `TimeEntrySuggestion`
 * card so a tech can sanity-check the summaries before posting to the PSA.
 *
 * The transcript is fetched on demand (not in the per-ticket list payload) so the
 * call-log endpoint stays cheap when a ticket has many historical calls.
 */
const CallReviewPanel: React.FC<ICallReviewPanelProps> = ({ call }) => {
  const paper = usePaperStyles();
  const dispatch = useAppDispatch();
  const transcript = useAppSelector(CallScribeTranscriptForCallSelector(call.id));
  const transcriptLoading = useAppSelector(CallScribeTranscriptLoadingForCallSelector(call.id));

  // `transcript === undefined` means we've never asked. `null` means the BE returned 404
  // (still processing). Anything else is a populated row.
  const transcriptFetched = transcript !== undefined;
  const [expanded, setExpanded] = React.useState<boolean>(false);

  // Auto-fetch on first expand so the body is ready when the panel animates open.
  useEffect(() => {
    if (expanded && !transcriptFetched && !transcriptLoading) {
      void dispatch(ThunkGetCallScribeTranscript(call.id));
    }
  }, [expanded, transcriptFetched, transcriptLoading, dispatch, call.id]);

  const onToggle = (): void => {
    setExpanded(prev => !prev);
  };

  const onRefresh = (): void => {
    void dispatch(ThunkGetCallScribeTranscript(call.id));
  };

  if (!call.hasIntelligence) {
    return null;
  }

  const transcriptText = transcript?.redactedText ?? transcript?.rawText ?? '';
  const caption = transcript ? transcriptCaption(transcript) : '';

  return (
    <div className={paper.subStack}>
      {call.summaryCustomerFacing ? (
        <div className={paper.quote}>
          <p className={paper.caption}>Customer-facing summary</p>
          <p className={paper.small}>{call.summaryCustomerFacing}</p>
        </div>
      ) : null}

      {call.summaryInternal ? (
        <div className={paper.quote}>
          <p className={paper.caption}>Internal summary</p>
          <p className={paper.small}>{call.summaryInternal}</p>
        </div>
      ) : null}

      <button type="button" className={paper.linkButton} onClick={onToggle} aria-expanded={expanded}>
        {expanded ? <ExpandLessRoundedIcon aria-hidden /> : <ExpandMoreRoundedIcon aria-hidden />}
        {expanded ? 'Hide the plain transcript' : 'Show the plain transcript'}
      </button>

      {expanded ? (
        <div>
          {transcriptLoading ? (
            <p className={paper.caption} role="status">Loading transcript…</p>
          ) : transcript === null ? (
            <p className={paper.caption}>
              Transcript not ready yet.{' '}
              <button type="button" className={paper.linkButton} onClick={onRefresh}>
                <RefreshRoundedIcon aria-hidden />
                Retry
              </button>
            </p>
          ) : transcript ? (
            <>
              {caption ? <p className={paper.caption}>{caption}</p> : null}
              <div className={paper.readOnlyBlock}>
                {transcriptText || 'This transcript is empty.'}
              </div>
            </>
          ) : (
            <p className={paper.caption}>Click Retry to load.</p>
          )}
        </div>
      ) : null}
    </div>
  );
};

export default CallReviewPanel;
